import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { NotificationKind, NotificationRead } from '@/client'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useNotifications } from '@/hooks/queries/notifications'
import { useTicketAccess } from '@/hooks/queries/tickets'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

import { useNotificationReads } from './notificationReads'

/** The badge stops counting here: `99+` stays one width in the header. */
const BADGE_MAX = 99

/** Each kind's glyph and its tint — one meaning per colour. */
const KIND_STYLE: Record<NotificationKind, { icon: string; tone: string }> = {
  ticket_assigned: { icon: 'assignment_ind', tone: 'bg-info-subtle text-info-strong' },
  ticket_comment: { icon: 'chat_bubble', tone: 'bg-primary-subtle text-primary-text' },
  ticket_mention: { icon: 'alternate_email', tone: 'bg-brand-subtle text-brand-text' },
  ai_analyst_ready: { icon: 'auto_awesome', tone: 'bg-success-subtle text-success-strong' },
  ai_analyst_failed: { icon: 'error', tone: 'bg-error-subtle text-error-foreground' },
  ai_analyst_alert: { icon: 'warning', tone: 'bg-warning-subtle text-warning-strong' },
}

type Filter = 'all' | 'unread'

/**
 * The header's bell — VentaHub's notification menu, rebuilt.
 *
 * Its feed is the server's (`useNotifications`): who assigned you a ticket,
 * commented on one you raised or own, or mentioned you. Which of those you
 * have read is this browser's, as it was in VentaHub. The badge counts the
 * unread; opening a notification marks it read and goes to its ticket.
 * VentaHub's New / Earlier split is kept, with Mark all as read and an
 * Unread filter added.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const access = useTicketAccess()
  const feed = useNotifications()
  const { read, markRead, markUnread } = useNotificationReads(access.data?.principal_id)

  const readIds = useMemo(() => new Set(read), [read])
  const items = useMemo(() => feed.data ?? [], [feed.data])
  const unread = items.filter((item) => !readIds.has(item.id))
  const earlier = items.filter((item) => readIds.has(item.id))
  const count = unread.length
  const badge = count > BADGE_MAX ? `${BADGE_MAX}+` : String(count)
  const now = new Date()

  const row = (item: NotificationRead) => (
    <NotificationRow
      key={item.id}
      item={item}
      isRead={readIds.has(item.id)}
      when={formatRelative(item.occurred_at, i18n.language, now)}
      onOpen={() => {
        markRead([item.id])
        setOpen(false)
      }}
      onToggleRead={() => (readIds.has(item.id) ? markUnread(item.id) : markRead([item.id]))}
    />
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={count ? t('notifications.openUnread', { count }) : t('notifications.open')}
        >
          <Icon name="notifications" filled={count > 0} />
          {count > 0 ? (
            <span
              aria-hidden="true"
              className="bg-destructive text-destructive-foreground ring-surface text-2xs absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 leading-none font-semibold tabular-nums ring-2"
            >
              {badge}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="flex max-h-[min(32rem,calc(100vh-5rem))] w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden p-0"
      >
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <h2 className="text-sm font-semibold">{t('notifications.title')}</h2>
          {count > 0 ? (
            <span className="bg-destructive-subtle text-destructive-strong rounded-full px-2 py-0.5 text-xs font-medium tabular-nums">
              {t('notifications.unreadCount', { count })}
            </span>
          ) : null}
          <Button
            variant="link"
            size="sm"
            className="ml-auto h-auto px-0"
            disabled={count === 0}
            onClick={() => markRead(unread.map((item) => item.id))}
          >
            {t('notifications.markAllRead')}
          </Button>
        </div>

        {items.length > 0 ? (
          <div className="border-b px-4 py-2">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              aria-label={t('notifications.filterLabel')}
              value={filter}
              onValueChange={(next) => {
                if (next) setFilter(next as Filter)
              }}
            >
              <ToggleGroupItem value="all">{t('notifications.all')}</ToggleGroupItem>
              <ToggleGroupItem value="unread">
                {t('notifications.unread')}
                {count > 0 ? <span className="tabular-nums"> · {badge}</span> : null}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {feed.isPending ? (
            <div aria-busy="true" className="space-y-1 p-2">
              <span className="sr-only">{t('notifications.loading')}</span>
              {[0, 1, 2].map((index) => (
                <div key={index} className="flex gap-3 p-2">
                  <Skeleton className="size-8 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : feed.isError ? (
            <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <Icon name="error" size={48} className="text-error-foreground" />
              <p className="text-muted-foreground text-sm">{t('notifications.failed')}</p>
              <Button variant="outline" size="sm" onClick={() => void feed.refetch()}>
                {t('notifications.retry')}
              </Button>
            </div>
          ) : items.length === 0 ? (
            <Empty title={t('notifications.emptyTitle')} body={t('notifications.emptyBody')} />
          ) : filter === 'unread' ? (
            unread.length ? (
              <ul>{unread.map(row)}</ul>
            ) : (
              <Empty title={t('notifications.emptyUnread')} />
            )
          ) : (
            <>
              {unread.length ? (
                <Section label={t('notifications.newer')}>{unread.map(row)}</Section>
              ) : null}
              {earlier.length ? (
                <Section label={t('notifications.earlier')}>{earlier.map(row)}</Section>
              ) : null}
            </>
          )}
        </div>

        {items.length > 0 ? (
          <p className="text-muted-foreground border-t px-4 py-2 text-center text-xs">
            {t('notifications.footnote')}
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section aria-label={label}>
      <h3 className="text-muted-foreground bg-popover sticky top-0 z-10 px-4 pt-3 pb-1 text-xs font-medium tracking-wide uppercase">
        {label}
      </h3>
      <ul>{children}</ul>
    </section>
  )
}

function Empty({ title, body }: { title: string; body?: string }) {
  return (
    <div role="status" className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <Icon name="notifications_off" size={48} className="text-muted-foreground" />
      <p className="text-sm font-medium">{title}</p>
      {body ? <p className="text-muted-foreground text-xs">{body}</p> : null}
    </div>
  )
}

/**
 * One notification: its kind's glyph, what happened, when, and the comment
 * it quotes. The whole row opens the ticket; the dot beside it toggles read.
 */
function NotificationRow({
  item,
  isRead,
  when,
  onOpen,
  onToggleRead,
}: {
  item: NotificationRead
  isRead: boolean
  when: string
  onOpen: () => void
  onToggleRead: () => void
}) {
  const { t } = useTranslation()
  const style = KIND_STYLE[item.kind]
  const ticket = t('notifications.ticket', { number: item.ticket_number ?? 0 })
  const body = t(`notifications.kind.${item.kind}.body`, {
    ticket,
    type: item.ticket_type_name ?? item.ticket_type_id ?? '',
    actor: item.actor_name ?? t('notifications.someone'),
    title: item.title ?? '',
  })
  const rowClass =
    'focus-visible:ring-ring flex min-w-0 flex-1 gap-3 px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-inset'
  const content = (
    <>
      <span
        className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', style.tone)}
      >
        <Icon name={style.icon} size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'truncate text-sm',
              isRead ? 'text-foreground-secondary' : 'font-semibold',
            )}
          >
            {t(`notifications.kind.${item.kind}.title`)}
          </span>
          <time
            dateTime={item.occurred_at}
            className="text-muted-foreground ml-auto shrink-0 text-xs tabular-nums"
          >
            {when}
          </time>
        </span>
        <span className="text-muted-foreground block text-sm">{body}</span>
        {item.excerpt ? (
          <span className="text-foreground-secondary bg-surface-muted mt-1.5 line-clamp-2 block rounded-sm px-2 py-1 text-xs">
            {item.excerpt}
          </span>
        ) : null}
      </span>
    </>
  )

  return (
    <li className="group hover:bg-surface-hover relative flex items-start border-b last:border-b-0">
      {item.conversation_id ? (
        <Link
          to="/ai-analyst/c/$conversationId"
          params={{ conversationId: item.conversation_id }}
          onClick={onOpen}
          className={rowClass}
        >
          {content}
        </Link>
      ) : item.ticket_id ? (
        <Link
          to="/tickets/$ticketId"
          params={{ ticketId: item.ticket_id }}
          onClick={onOpen}
          className={rowClass}
        >
          {content}
        </Link>
      ) : (
        <div className={rowClass}>{content}</div>
      )}
      <button
        type="button"
        onClick={onToggleRead}
        aria-label={isRead ? t('notifications.markUnread') : t('notifications.markRead')}
        title={isRead ? t('notifications.markUnread') : t('notifications.markRead')}
        className="focus-visible:ring-ring mt-3 mr-3 flex size-6 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2"
      >
        <span
          aria-hidden="true"
          className={cn(
            'size-2 rounded-full transition-colors',
            isRead ? 'border-border-strong border group-hover:bg-transparent' : 'bg-brand',
          )}
        />
      </button>
    </li>
  )
}
